import { useEffect, useRef } from 'react'
import Highlighter from 'react-highlight-words'
import { RxArrowLeft } from 'react-icons/rx'
import { Else, If, Then } from 'react-if'
import Typography from '~/@/components/ui/typography'
import { cn } from '~/@/lib/utils'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { getIconByColumnType } from '~/common/utils/table'
import { AllowedArrayInnerTypes } from '~/common/utils/table-column'
import { LookupProvider } from '~/components/records/lookup/hooks/LookupProvider'
import { DetailedTable } from '~/components/tables/types'
import { IconButton } from '~/components/ui/atoms/icon-button/IconButton'
import { Tooltip } from '../../ui/atoms/tooltip/Tooltip'
import { LookupColumnPreviewData } from './LookupColumnPreviewData'
import { LookupTableAndFieldDropdownEntity } from './LookupTableAndFieldDropdown'
import { AllTablesQueryTableColumn } from './types'

interface LookupFieldsPopoverContentProps {
  highlightedOption: AllTablesQueryTableColumn | null
  setSearchQuery: (searchQuery: string) => void
  handleClose: () => void
  searchQuery: string
  setHighlightedOption: (option: AllTablesQueryTableColumn | null) => void
  setDropdownEntity?: (entity: LookupTableAndFieldDropdownEntity) => void
  columnOptions: AllTablesQueryTableColumn[]
  showDropdownHeaderArrowButton: boolean
  shouldDisableOption: (columnId: string) => boolean
  lookupTable?: Pick<DetailedTable, 'id'> | null
  selectedColumnId: string | null
  onColumnSelect: (columnId: string) => void
}

/**
 * Renders the dropdown menu items for the lookup field selection
 */
export function LookupFieldsPopoverContent({
  highlightedOption,
  setSearchQuery,
  handleClose,
  searchQuery,
  shouldDisableOption,
  setHighlightedOption,
  setDropdownEntity,
  columnOptions,
  showDropdownHeaderArrowButton,
  lookupTable,
  selectedColumnId,
  onColumnSelect,
}: LookupFieldsPopoverContentProps) {
  const highlightedRef = useRef<HTMLDivElement>(null)
  const { t } = useTypedTranslation()
  // Scroll to hihglighted option
  useEffect(() => {
    if (highlightedRef.current) {
      highlightedRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [highlightedOption])

  const handleTableColumnSelection = (tableColumnId: string) => {
    onColumnSelect(tableColumnId)
    setSearchQuery('')
    handleClose()
  }

  const isSelected = (tableColumnId: string) => {
    return selectedColumnId === tableColumnId
  }

  const selectedOption = columnOptions.find((column) => isSelected(column.id))

  return (
    <div className="flex size-full grow flex-col overflow-hidden outline-none">
      <div
        className={`flex shrink-0 items-center gap-1 border-b border-gray-4 bg-gray-1 ${!showDropdownHeaderArrowButton ? 'p-2' : 'py-1'}`}
      >
        {showDropdownHeaderArrowButton && (
          <IconButton
            onClick={() => {
              if (setDropdownEntity) {
                setDropdownEntity(LookupTableAndFieldDropdownEntity.TABLE)
              }
              setSearchQuery('')
            }}
            size="small"
            variant="true-ghost"
          >
            <RxArrowLeft size={12} />
          </IconButton>
        )}
        <Typography className="text-gray-12" variant="body3">
          {t('table_column.select_a_field')}
        </Typography>
      </div>
      <div className="flex grow items-stretch overflow-hidden">
        <If condition={columnOptions.length === 0}>
          <Then>
            <div className="flex grow items-center justify-center">
              {t('table_column.no_fields_found')}
            </div>
          </Then>
          <Else>
            <div className="flex w-1/2 flex-col overflow-hidden">
              <div className="grow overflow-y-auto">
                {columnOptions.map((column) => (
                  <Tooltip
                    key={column.id}
                    placement="left"
                    title={
                      shouldDisableOption(column.id)
                        ? t(
                            'table_column.field_cannot_be_selected_it_already_has_a_lookup_associated_to_it',
                          )
                        : ''
                    }
                  >
                    <div
                      ref={column.id === highlightedOption?.id ? highlightedRef : null}
                      className={cn(
                        'flex gap-2 items-center p-2 text-nowrap truncate',
                        isSelected(column.id) && 'bg-blue-2',
                        column.id === highlightedOption?.id && 'bg-gray-1',
                        shouldDisableOption(column.id)
                          ? 'text-gray-8 cursor-default hover:bg-transparent bg-transparent'
                          : 'cursor-pointer hover:bg-gray-1',
                      )}
                      onClick={() => {
                        if (!shouldDisableOption(column.id)) {
                          handleTableColumnSelection(column.id)
                        }
                      }}
                      onMouseEnter={() => {
                        if (!shouldDisableOption(column.id)) {
                          setHighlightedOption(column)
                        }
                      }}
                      onMouseLeave={() => setHighlightedOption(null)}
                    >
                      <div className="shrink-0">
                        {getIconByColumnType(
                          column.type,
                          column.innerType as AllowedArrayInnerTypes,
                        )}
                      </div>
                      <Typography
                        className={`truncate text-nowrap ${shouldDisableOption(column.id) ? 'cursor-default bg-transparent text-gray-8 hover:bg-transparent' : ''}`}
                        variant="body3"
                      >
                        <Highlighter
                          autoEscape
                          highlightStyle={{
                            backgroundColor: 'transparent',
                            fontWeight: 600,
                          }}
                          searchWords={[searchQuery]}
                          textToHighlight={column.name}
                        />
                      </Typography>
                    </div>
                  </Tooltip>
                ))}
              </div>
            </div>
            <LookupProvider columns={columnOptions} isUnauth={false}>
              <LookupColumnPreviewData
                highlightedOption={highlightedOption}
                lookupTable={lookupTable}
                selectedOption={selectedOption}
                showDisabledState={shouldDisableOption(highlightedOption?.id ?? '')}
              />
            </LookupProvider>
          </Else>
        </If>
      </div>
    </div>
  )
}
